import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, X, ShieldCheck, BarChart3, Target, ChevronDown } from 'lucide-react';
import {
  getStoredConsent,
  setStoredConsent,
  hasConsentDecision,
  defaultConsent,
} from '../utils/consent';

/**
 * GDPR cookie banner with three categories:
 *   1. Necessary (always on)
 *   2. Analytics (Google Analytics, etc.)
 *   3. Marketing (remarketing, Meta Pixel)
 *
 * Shows on first visit only; dismissal stamps localStorage. Respects an
 * existing decision. Talks Google Consent Mode v2 via `pushConsentToGtag`
 * so ad tags can load/suppress without a reload.
 */
const CookieBanner = () => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(defaultConsent());

  useEffect(() => {
    if (!hasConsentDecision()) {
      setVisible(true);
    } else {
      const stored = getStoredConsent();
      if (stored) setPrefs(stored);
    }
  }, []);

  const acceptAll = () => {
    setStoredConsent({ analytics: true, marketing: true });
    setVisible(false);
  };

  const rejectAll = () => {
    setStoredConsent({ analytics: false, marketing: false });
    setVisible(false);
  };

  const savePrefs = () => {
    setStoredConsent({ analytics: prefs.analytics, marketing: prefs.marketing });
    setVisible(false);
  };

  if (!visible) return null;

  const categories = [
    {
      key: 'necessary',
      icon: ShieldCheck,
      title: t('cookies.necessary_title', 'Nevyhnutné'),
      desc: t(
        'cookies.necessary_desc',
        'Potrebné pre fungovanie webu (prihlásenie, košík). Nemožno vypnúť.',
      ),
      locked: true,
    },
    {
      key: 'analytics',
      icon: BarChart3,
      title: t('cookies.analytics_title', 'Analytické'),
      desc: t(
        'cookies.analytics_desc',
        'Anonymné štatistiky návštevnosti na zlepšenie obsahu (Google Analytics).',
      ),
      locked: false,
    },
    {
      key: 'marketing',
      icon: Target,
      title: t('cookies.marketing_title', 'Marketingové'),
      desc: t(
        'cookies.marketing_desc',
        'Personalizovaná reklama a remarketing (Google Ads, Meta Pixel).',
      ),
      locked: false,
    },
  ];

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[9999] p-3 sm:p-4"
      data-testid="cookie-banner"
    >
      <div className="mx-auto max-w-4xl bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-3 p-4 sm:p-5 border-b border-gray-100">
          <div className="flex-shrink-0 h-10 w-10 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-semibold text-gray-900">
              {t('cookies.title', 'Používame cookies')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {t(
                'cookies.intro',
                'Na našom webe používame cookies pre zlepšenie vášho zážitku. Niektoré sú nevyhnutné, iné nám pomáhajú s analytikou a marketingom.',
              )}{' '}
              <a
                href="/legal/privacy"
                target="_blank"
                rel="noreferrer"
                className="text-orange-600 hover:text-orange-700 underline"
              >
                {t('cookies.read_privacy', 'Viac v Ochrane osobných údajov')}
              </a>
              .
            </p>
          </div>
          <button
            onClick={rejectAll}
            aria-label="Close"
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1"
            data-testid="cookie-close-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Details (expandable) */}
        {showDetails && (
          <div className="px-4 sm:px-5 py-4 border-b border-gray-100 space-y-3 bg-gray-50">
            {categories.map(({ key, icon: Icon, title, desc, locked }) => (
              <label
                key={key}
                className="flex items-start gap-3 p-3 rounded-lg bg-white border border-gray-200"
                data-testid={`cookie-row-${key}`}
              >
                <div className="flex-shrink-0 h-8 w-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="text-sm font-semibold text-gray-900">{title}</div>
                    <input
                      type="checkbox"
                      checked={locked ? true : prefs[key]}
                      disabled={locked}
                      onChange={(e) =>
                        !locked && setPrefs({ ...prefs, [key]: e.target.checked })
                      }
                      className="h-4 w-4 accent-orange-500"
                      data-testid={`cookie-toggle-${key}`}
                    />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{desc}</div>
                </div>
              </label>
            ))}
          </div>
        )}

        {/* Actions */}
        <div className="p-4 sm:p-5 flex flex-col sm:flex-row sm:items-center gap-3">
          <button
            onClick={() => setShowDetails((v) => !v)}
            className="inline-flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 order-3 sm:order-1 sm:mr-auto"
            data-testid="cookie-details-btn"
          >
            <ChevronDown
              className={`h-4 w-4 transition ${showDetails ? 'rotate-180' : ''}`}
            />
            {showDetails
              ? t('cookies.hide_details', 'Skryť detaily')
              : t('cookies.customize', 'Prispôsobiť')}
          </button>
          <button
            onClick={rejectAll}
            className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 border border-gray-300 rounded-md order-2 sm:order-2"
            data-testid="cookie-reject-btn"
          >
            {t('cookies.reject', 'Odmietnuť voliteľné')}
          </button>
          {showDetails ? (
            <button
              onClick={savePrefs}
              className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 order-1 sm:order-3 font-medium"
              data-testid="cookie-save-btn"
            >
              {t('cookies.save', 'Uložiť výber')}
            </button>
          ) : (
            <button
              onClick={acceptAll}
              className="px-4 py-2 text-sm bg-orange-500 text-white rounded-md hover:bg-orange-600 order-1 sm:order-3 font-medium"
              data-testid="cookie-accept-btn"
            >
              {t('cookies.accept_all', 'Prijať všetky')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
